import { useMemo } from "react";
import {
  Box,
  Link,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import type { DashboardCalendarEvent } from "@services/facilityDashboard";
import {
  formatLocalDateIso,
  mergeCalendarEventsForDay,
} from "./dashboardComplianceCalendarUtils";
import { DASHBOARD, getDashboardWhiteCardStyles } from "./dashboardTheme";

const Card = styled(Box)(({ theme }) => getDashboardWhiteCardStyles(theme));

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function severityColor(events: DashboardCalendarEvent[]): string | undefined {
  if (events.some((e) => e.status === "overdue")) return "#fecaca";
  if (events.some((e) => e.event_type === "required")) return "#fde68a";
  if (events.some((e) => e.event_type === "recommended")) return "#bbf7d0";
  return undefined;
}

/** Badge background color matches the cell's severity tone (Figma spec). */
function severityBadgeColor(events: DashboardCalendarEvent[]): string {
  if (events.some((e) => e.status === "overdue")) return "#991b1b";
  if (events.some((e) => e.event_type === "required")) return "#92400e";
  if (events.some((e) => e.event_type === "recommended")) return "#166534";
  return DASHBOARD.header;
}

function build30DayGrid(reference: Date): Date[] {
  const start = new Date(reference);
  start.setHours(0, 0, 0, 0);
  const dow = start.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  start.setDate(start.getDate() + mondayOffset);
  const days: Date[] = [];
  for (let i = 0; i < 35; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function formatDateRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startStr = start.toLocaleDateString(undefined, opts);
  const endStr = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

export default function DashboardComplianceCalendarStrip({
  events,
  complianceEventCountsByDate,
  complianceEventsByDate,
  onViewFullCalendar,
  onDayClick,
}: {
  /** @deprecated Not used for display; range is computed from today. */
  monthLabel?: string;
  /** @deprecated Not used for display; range is computed from today. */
  year?: number;
  events: DashboardCalendarEvent[];
  complianceEventCountsByDate?: Map<string, number>;
  complianceEventsByDate?: Map<string, DashboardCalendarEvent[]>;
  onViewFullCalendar?: () => void;
  onDayClick?: (dateIso: string) => void;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const rangeEnd = useMemo(() => {
    const end = new Date(today);
    end.setDate(today.getDate() + 29);
    return end;
  }, [today]);

  const todayIso = useMemo(() => formatLocalDateIso(today), [today]);
  const rangeEndIso = useMemo(() => formatLocalDateIso(rangeEnd), [rangeEnd]);
  const rangeLabel = useMemo(() => formatDateRange(today, rangeEnd), [today, rangeEnd]);
  const gridDays = useMemo(() => build30DayGrid(today), [today]);

  const isFirstOfMonthInGrid = useMemo(() => {
    const seen = new Set<number>();
    return gridDays.map((d) => {
      const m = d.getMonth();
      if (!seen.has(m)) { seen.add(m); return true; }
      return false;
    });
  }, [gridDays]);

  const eventsByDate = useMemo(() => {
    const m = new Map<string, DashboardCalendarEvent[]>();
    for (const ev of events) {
      const list = m.get(ev.date) ?? [];
      list.push(ev);
      m.set(ev.date, list);
    }
    return m;
  }, [events]);

  return (
    <Card>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: DASHBOARD.header }}>
            30-day Compliance Calendar
          </Typography>
          <Typography sx={{ fontSize: 13, color: DASHBOARD.accent }}>
            {rangeLabel}
          </Typography>
        </Box>
        {onViewFullCalendar ? (
          <Link
            component="button"
            type="button"
            onClick={onViewFullCalendar}
            sx={{
              color: DASHBOARD.accent,
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View full calendar →
          </Link>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          mt: 2,
          border: `1px solid ${DASHBOARD.borderSubtle}`,
          borderRadius: 4,
          overflow: "hidden",
          bgcolor: DASHBOARD.listItemBg,
          "& > *": {
            borderRight: `1px solid ${DASHBOARD.borderSubtle}`,
            borderBottom: `1px solid ${DASHBOARD.borderSubtle}`,
            minHeight: 56,
            boxSizing: "border-box",
          },
          "& > *:nth-of-type(7n)": { borderRight: "none" },
          "& > *:nth-last-of-type(-n+7)": { borderBottom: "none" },
        }}
      >
        {WEEKDAYS.map((d, i) => (
          <Typography
            key={`${d}-${i}`}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: DASHBOARD.textMuted,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 0.5,
              bgcolor: DASHBOARD.surfaceMuted,
            }}
          >
            {d}
          </Typography>
        ))}

        {gridDays.map((d, idx) => {
          const iso = formatLocalDateIso(d);
          const bffEvents = eventsByDate.get(iso) ?? [];
          const parallelEvents = complianceEventsByDate?.get(iso);
          const dayEvents = mergeCalendarEventsForDay(bffEvents, parallelEvents);
          const eventCount = complianceEventCountsByDate?.get(iso) ?? dayEvents.length;
          const bg = severityColor(dayEvents);
          const badgeBg = severityBadgeColor(dayEvents);
          const isToday = iso === todayIso;
          const inRange = iso >= todayIso && iso <= rangeEndIso;
          const active = inRange || isToday;
          const hasEvents = eventCount > 0;
          const showMonthLabel = isFirstOfMonthInGrid[idx];

          const cell = (
            <Box
              key={iso}
              onClick={() => hasEvents && onDayClick?.(iso)}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: bg ?? (active ? DASHBOARD.listItemBg : DASHBOARD.surfaceMuted),
                cursor: hasEvents ? "pointer" : "default",
                "&:hover": hasEvents ? { filter: "brightness(0.97)" } : undefined,
              }}
            >
              {/* Month label + date stacked and centered; badge overlaid at top-right */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.25,
                }}
              >
                {showMonthLabel ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: active ? DASHBOARD.accent : DASHBOARD.textMuted,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      lineHeight: 1,
                      userSelect: "none",
                    }}
                  >
                    {MONTH_ABBR[d.getMonth()]}
                  </Typography>
                ) : null}

                {isToday ? (
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor: DASHBOARD.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1,
                      userSelect: "none",
                    }}
                  >
                    {d.getDate()}
                  </Box>
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      color: active ? DASHBOARD.header : DASHBOARD.textMuted,
                      lineHeight: 1,
                      userSelect: "none",
                    }}
                  >
                    {d.getDate()}
                  </Typography>
                )}
              </Box>

              {/* Event count badge — absolute top-right */}
              {hasEvents ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: badgeBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                >
                  {eventCount}
                </Box>
              ) : null}
            </Box>
          );

          if (!hasEvents) return cell;
          const tip =
            dayEvents.length > 0
              ? dayEvents.map((e) => `${e.name} (${e.event_type}, ${e.status})`).join("\n")
              : `${eventCount} deadline${eventCount === 1 ? "" : "s"}`;
          return (
            <Tooltip key={iso} title={<span style={{ whiteSpace: "pre-line" }}>{tip}</span>}>
              {cell}
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
        {[
          { color: "#fecaca", label: "Overdue", bordered: false },
          { color: "#fde68a", label: "Required deadline", bordered: false },
          { color: "#bbf7d0", label: "Recommended", bordered: false },
          { color: "#f3f4f6", label: "Clear", bordered: true },
          { color: DASHBOARD.accent, label: "Today", bordered: false },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: item.color,
                border: item.bordered ? `1px solid ${DASHBOARD.borderSubtle}` : "none",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 12, color: DASHBOARD.text }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
